import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { invoicesApi, customersApi, paymentsApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Bell, CreditCard, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { fmtMoney, fmtDate, fmtDocNumber } from '@/lib/format';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { INVOICES } from '@/constants/testIds';

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, method: 'card', note: '' });

  const load = async () => {
    try {
      const [ivs, cs] = await Promise.all([invoicesApi.list(), customersApi.list()]);
      setItems(ivs);
      setCustomers(cs);
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const remind = async (id) => { await invoicesApi.remind(id); toast.success('Reminder queued'); };

  const openPayment = (inv) => {
    setPayFor(inv);
    setPayForm({ amount: inv.total - (inv.total_paid || 0), method: 'card', note: '' });
    setPayOpen(true);
  };
  const submitPayment = async (e) => {
    e.preventDefault();
    try {
      await paymentsApi.create({ invoice_id: payFor.id, amount: parseFloat(payForm.amount) || 0, method: payForm.method, note: payForm.note });
      toast.success('Payment recorded');
      setPayOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <AppShell title="Invoices">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold">Invoices</h1>
        <p className="text-sm text-muted-foreground">Track billing and payments.</p>
      </div>
      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" description="Invoices are auto-created when a job reaches 'Invoiced'." />
      ) : (
        <Card><CardContent className="p-0">
          <div className="divide-y">
            {items.map(i => {
              const c = customers.find(x => x.id === i.customer_id);
              return (
                <div key={i.id} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 transition-colors hover:bg-muted/40">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{fmtDocNumber(i, 'Invoice')} · {c?.name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(i.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{i.status}</Badge>
                    {/* The amount is the point of the row — give it the weight. */}
                    <div className="font-display text-base font-semibold tabular-nums mr-auto sm:mr-0 sm:w-24 sm:text-right">{fmtMoney(i.total)}</div>
                    <Button size="sm" variant="outline" onClick={() => remind(i.id)} data-testid={INVOICES.remindButton}><Bell className="h-3 w-3 mr-1" />Remind</Button>
                    {i.status !== 'paid' && <Button size="sm" onClick={() => openPayment(i)} data-testid={INVOICES.addPayment}><CreditCard className="h-3 w-3 mr-1" />Record payment</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent></Card>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
          <form onSubmit={submitPayment} className="space-y-3">
            <div><Label>Amount *</Label><Input type="number" step="0.01" required value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} /></div>
            <div><Label>Method</Label>
              <select className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong" value={payForm.method} onChange={e => setPayForm({ ...payForm, method: e.target.value })}>
                <option value="card">Card</option><option value="cash">Cash</option><option value="transfer">Transfer</option><option value="other">Other</option>
              </select>
            </div>
            <div><Label>Note</Label><Input value={payForm.note} onChange={e => setPayForm({ ...payForm, note: e.target.value })} /></div>
            <DialogFooter><Button type="submit">Record</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
